import { api } from './client'
import type { ApiEnvelope, PaginatedResponse } from '@/types/api'
import type { AxiosError } from 'axios'

export interface RobotLocation {
    id: string
    name: string
    x: number
    y: number
}

export interface RobotLocationQuery {
    page?: number
    size?: number
    locationName?: string
}

export interface AddRobotLocationPayload {
    name: string
    latitude: number
    longitude: number
    yaw: number
}

export interface UpdateRobotLocationPayload {
    id: string
    name: string
    latitude: number
    longitude: number
    yaw: number
    status?: number
}

export interface RobotHomeLocation {
    latitude: number
    longitude: number
    yaw: number
}

export interface HomeLocationPayload {
    latitude: number
    longitude: number
    yaw: number
}

function unwrapSuccessEnvelopeFromAxiosError<T>(error: unknown): ApiEnvelope<T> | null {
    const axiosError = error as AxiosError<ApiEnvelope<T>>
    const data = axiosError.response?.data
    if (data?.responseCode === 'OK') {
        return data
    }
    return null
}

function mapLocationData(
    data: any,
    fallback: { id?: string; name: string; longitude: number; latitude: number; yaw?: number; status?: number },
): RobotLocation {
    return {
        id: String(data?.id ?? fallback.id ?? ''),
        name: data?.name ?? fallback.name,
        x: Number.isNaN(Number(data?.longitude)) ? fallback.longitude : Number(data.longitude),
        y: Number.isNaN(Number(data?.latitude)) ? fallback.latitude : Number(data.latitude),
    }
}

async function findLocationIdByCoordinates(
    robotId: string,
    latitude: number,
    longitude: number,
): Promise<string | null> {
    const list = await fetchRobotLocations(robotId, { page: 0, size: 10 })
    const EPS = 0.0002
    const found = list.items.find((item) =>
        Math.abs(item.x - longitude) <= EPS && Math.abs(item.y - latitude) <= EPS,
    )
    return found?.id ?? null
}

export async function addRobotLocation(robotId: string, payload: AddRobotLocationPayload): Promise<RobotLocation> {
    try {
        const response = await api.post<ApiEnvelope<any>>(`/v1/robot/location/add/${robotId}`, payload)
        const data = response.data.responseData

        if (data?.id) {
            return mapLocationData(data, {
                name: payload.name,
                longitude: payload.longitude,
                latitude: payload.latitude,
            })
        }

        const fallbackId = await findLocationIdByCoordinates(
            robotId,
            payload.latitude,
            payload.longitude,
        )

        return mapLocationData(data, {
            id: fallbackId ?? `tmp-${Date.now()}`,
            name: payload.name,
            longitude: payload.longitude,
            latitude: payload.latitude,
        })
    } catch (error) {
        const okEnvelope = unwrapSuccessEnvelopeFromAxiosError<any>(error)
        if (!okEnvelope) throw error

        const data = okEnvelope.responseData
        if (data?.id) {
            return mapLocationData(data, {
                name: payload.name,
                longitude: payload.longitude,
                latitude: payload.latitude,
            })
        }

        const fallbackId = await findLocationIdByCoordinates(
            robotId,
            payload.latitude,
            payload.longitude,
        )

        return mapLocationData(data, {
            id: fallbackId ?? `tmp-${Date.now()}`,
            name: payload.name,
            longitude: payload.longitude,
            latitude: payload.latitude,
        })
    }
}

export async function updateRobotLocation(
    robotId: string,
    payload: UpdateRobotLocationPayload,
): Promise<RobotLocation> {
    try {
        const response = await api.put<ApiEnvelope<any>>(`/v1/robot/location/update/${robotId}`, payload)
        return mapLocationData(response.data.responseData, {
            id: payload.id,
            name: payload.name.trim(),
            longitude: payload.longitude,
            latitude: payload.latitude,
            yaw: payload.yaw,
            status: payload.status,
        })
    } catch (error) {
        const okEnvelope = unwrapSuccessEnvelopeFromAxiosError<any>(error)
        if (!okEnvelope) throw error
        return mapLocationData(okEnvelope.responseData, {
            id: payload.id,
            name: payload.name,
            longitude: payload.longitude,
            latitude: payload.latitude,
            yaw: payload.yaw,
            status: payload.status,
        })
    }
}

export async function deleteRobotLocation(robotId: string, locationId: string): Promise<void> {
    try {
        await api.delete(`/v1/robot/location/delete/${robotId}/${locationId}`)
    } catch (error) {
        const okEnvelope = unwrapSuccessEnvelopeFromAxiosError<any>(error)
        if (!okEnvelope) throw error
    }
}

export async function fetchRobotLocations(robotId: string, query: RobotLocationQuery = {}, signal?: AbortSignal): Promise<PaginatedResponse<RobotLocation>> {
    const { page = 0, size = 10, locationName } = query
    try {

        const response = await api.get<ApiEnvelope<any>>(`/v1/robot/location/search/${robotId}`, {
            params: { page, size, locationName, searchType: 'ALL' },
            signal
        })
        const data = response.data.responseData
        return {
            items: Array.isArray(data.content)
                ? data.content.map((item: any) => ({
                    id: String(item.id),
                    name: item.name,
                    x: Number.isNaN(Number(item.longitude)) ? null : Number(item.longitude),
                    y: Number.isNaN(Number(item.latitude)) ? null : Number(item.latitude),
                }))
                : [],
            total: data.totalElements,
            page: data.number + 1,
            limit: size,
            totalPages: data.totalPages,
        }
    } catch (error: any) {
        const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi lấy danh sách vị trí.';
        throw new Error(message);
    }
}

export async function fetchRobotHomeLocation(robotId: string, signal?: AbortSignal): Promise<RobotHomeLocation | null> {
    try {
        const response = await api.get<ApiEnvelope<any>>(`/v1/robot/location-home/detail/${robotId}`, { signal })
        const data = response.data.responseData
        if (!data || data.latitude == null || data.longitude == null) return null
        return {
            latitude: Number(data.latitude),
            longitude: Number(data.longitude),
            yaw: Number(data.yaw ?? 0),
        }
    } catch {
        return null
    }
}

export async function addRobotHomeLocation(robotId: string, payload: HomeLocationPayload): Promise<void> {
    try {
        await api.post<ApiEnvelope<any>>(`/v1/robot/location-home/add/${robotId}`, payload)
    } catch (error) {
        const okEnvelope = unwrapSuccessEnvelopeFromAxiosError<any>(error)
        if (!okEnvelope) throw error
    }
}

export async function updateRobotHomeLocation(robotId: string, payload: HomeLocationPayload): Promise<void> {
    try {
        await api.put<ApiEnvelope<any>>(`/v1/robot/location-home/update/${robotId}`, payload)
    } catch (error) {
        const okEnvelope = unwrapSuccessEnvelopeFromAxiosError<any>(error)
        if (!okEnvelope) throw error
    }
}
